/**
 * Trattative sul computer, ridisegnate il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: quanto possiamo chiudere, e chi chiamo?
 * La forma: la bacheca della pipeline a colonne, ognuna col suo totale, e
 * schede con valore e prossima azione. In testa la risposta in una frase e una
 * striscia "quando si chiudono" che non dice solo zero: se le date previste sono
 * passate (il 30/09 dei voucher) lo dice e le conta, invece di "in ritardo di 26
 * giorni" su ogni riga. La data giusta la decide chi segue la trattativa.
 *
 * Alleggerita (Lorenzo, 26/10): via i quattro numeri in testa, le linguette
 * In corso/Vinte/Chiuse/Tutte sopra la bacheca, "Ordina" e "Filtra per", la
 * fascia di chiamata, i pallini dei requisiti senza legenda, "40k/20k",
 * l'aggiunta veloce. I filtri vecchi nell'indirizzo (owner, fascia, stale,
 * solo, from, to) valgono ancora: se ce n'è uno acceso una riga lo dice.
 * Le colonne vuote si stringono a una striscia: si vedono tutte le fasi senza
 * far scorrere la bacheca per niente. Sul telefono resta TrattativeMobile.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Avatar } from '../../components/ui/Avatar';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { EmptyState } from '../../components/ui/EmptyState';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { leggibile } from '../../components/mobile/MobileKit';
import { useUrlState } from '../../hooks/useUrlState';
import { useKanbanDnd } from '../../lib/dnd';
import { euro, fmtDate } from '../../lib/utils';
import {
  dealsSummaryQuery, dealsListQuery, pipelinesQuery,
  canWriteDeals, counterparty, daysFromToday, exitStages, fullName, pathStages,
  type DealItem, type DealView, type Pipeline,
} from './dealsApi';
import { useDealActions } from './useDealActions';
import { ClosePopover } from './ClosePopover';
import { MovePipelinePopover } from './MovePipelinePopover';
import { NextActionPopover } from './NextActionPopover';
import './trattative-desktop.css';

const LAYOUT_KEY = 'deals-view';
const PIPELINE_KEY = 'deals-pipeline';
const CHIUSE = 'CHIUSE';
const VISTE_LISTA: Array<{ k: DealView; label: string }> = [
  { k: 'aperte', label: 'In corso' },
  { k: 'vinte', label: 'Vinte' },
  { k: 'chiuse', label: 'Chiuse' },
];
const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

/** "Ott-Nov 2026" → "ottobre e novembre"; tre mesi → "da febbraio ad aprile". Anno solo se non è quello di oggi. */
function periodo(from: string, to: string): string {
  const a = Number(from.slice(5, 7)) - 1;
  const b = Number(to.slice(5, 7)) - 1;
  const ya = Number(from.slice(0, 4));
  const yb = Number(to.slice(0, 4));
  const oggi = new Date().getFullYear();
  const anno = yb !== oggi ? ` ${yb}` : '';
  if (ya !== yb) return `${MESI[a]} ${ya} e ${MESI[b]} ${yb}`;
  const span = b - a;
  if (span <= 1) return `${MESI[a]} e ${MESI[b]}${anno}`;
  return `da ${MESI[a]} a ${MESI[b]}${anno}`;
}

/** Per la tessera: "Entro novembre" se il periodo comincia adesso, altrimenti "Dicembre e gennaio", "Febbraio-aprile". */
function periodoBreve(from: string, to: string): string {
  const oggi = new Date();
  const meseOggi = oggi.getFullYear() * 12 + oggi.getMonth();
  const a = Number(from.slice(0, 4)) * 12 + Number(from.slice(5, 7)) - 1;
  const b = Number(to.slice(0, 4)) * 12 + Number(to.slice(5, 7)) - 1;
  const nome = (m: number) => MESI[((m % 12) + 12) % 12]!;
  const su = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (a <= meseOggi) return `Entro ${nome(b)}`;
  if (b - a === 1) return su(`${nome(a)} e ${nome(b)}`);
  return su(`${nome(a)}-${nome(b)}`);
}

/** La data di chiusura prevista è già passata (e la trattativa è ancora aperta). */
const dataPassata = (d: DealItem) => d.isOpen && (daysFromToday(d.expectedCloseDate) ?? 0) < 0;

/** Quando, in parole: "oggi", "domani", "giovedì 29", "da 3 giorni". */
function quando(iso: string | null | undefined): { testo: string; alert?: boolean } {
  const g = daysFromToday(iso);
  if (g == null) return { testo: 'senza data' };
  if (g < 0) return { testo: g === -1 ? 'da ieri' : `da ${-g} giorni`, alert: true };
  if (g === 0) return { testo: 'oggi' };
  if (g === 1) return { testo: 'domani' };
  const d = new Date(`${iso!.slice(0, 10)}T12:00:00`);
  if (g <= 6) return { testo: d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric' }) };
  return { testo: d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }) };
}

export function TrattativeDesktop() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const canWrite = canWriteDeals(user);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: pipelines } = useQuery(pipelinesQuery);
  const pipelineParam = searchParams.get('pipeline') ?? '';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const viewParam = searchParams.get('view') ?? 'aperte';
  const view: DealView = (['aperte', 'vinte', 'chiuse', 'tutte'] as string[]).includes(viewParam) ? (viewParam as DealView) : 'aperte';

  // Filtri nascosti, ancora validi dall'indirizzo, e quelli della striscia.
  const p = (k: string) => searchParams.get(k) ?? '';
  const owner = p('owner'); const fascia = p('fascia'); const stale = p('stale') === '1';
  const solo = p('solo'); const from = p('from'); const to = p('to');
  const chiusura = p('chiusura'); // 'passata': solo le date già passate (si filtra qui)

  const setParams = (next: Record<string, string>) => setSearchParams((prev) => {
    const sp = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(next)) { if (v) sp.set(k, v); else sp.delete(k); }
    return sp;
  });
  const togliFiltri = () => setParams({ owner: '', fascia: '', stale: '', solo: '', from: '', to: '', chiusura: '' });

  // La pipeline: quella dell'indirizzo, l'ultima guardata, o la più piena.
  useEffect(() => {
    if (!pipelines?.length || pipelineParam) return;
    let saved: string | null = null;
    try { saved = localStorage.getItem(PIPELINE_KEY); } catch { /* niente */ }
    if (saved === '') return; // ha scelto "Tutte" l'ultima volta
    const scelta = (saved && pipelines.find((x) => x.slug === saved)) || [...pipelines].sort((a, b) => (b.dealsCount ?? 0) - (a.dealsCount ?? 0))[0];
    if (scelta) setParams({ pipeline: scelta.slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelines]);
  const scegliPipeline = (slug: string) => {
    try { localStorage.setItem(PIPELINE_KEY, slug); } catch { /* niente */ }
    setParams({ pipeline: slug, from: '', to: '' });
  };
  const pipeline: Pipeline | undefined = useMemo(() => (pipelines ?? []).find((x) => x.slug === pipelineParam), [pipelines, pipelineParam]);

  const [layoutPref, setLayoutPref] = useState<'list' | 'kanban'>(() => {
    try { return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'kanban'; } catch { return 'kanban'; }
  });
  const setLayout = (v: 'list' | 'kanban') => { setLayoutPref(v); try { localStorage.setItem(LAYOUT_KEY, v); } catch { /* niente */ } };
  // La bacheca vuole una pipeline sola: le colonne sono le sue fasi.
  const layout: 'list' | 'kanban' = pipeline ? layoutPref : 'list';

  const { data: summary } = useQuery(dealsSummaryQuery(pipelineParam || undefined));
  // Le aperte della pipeline, senza filtri: servono alla frase e alla striscia.
  const aperteQ = useQuery(dealsListQuery({ view: 'aperte', pipeline: pipelineParam || undefined, sort: 'chiamate', page: 1, pageSize: 500 }));
  const params = {
    view: layout === 'kanban' ? ('tutte' as DealView) : view,
    pipeline: pipelineParam || undefined,
    search: search.trim() || undefined,
    ownerId: owner || undefined,
    fascia: fascia || undefined,
    stale: stale || undefined,
    solo: solo || undefined,
    from: from || undefined,
    to: to || undefined,
    sort: 'chiamate',
    page: 1,
    pageSize: 500,
  };
  const itemsQ = useQuery(dealsListQuery(params));
  const items = useMemo(() => {
    const all = itemsQ.data?.items ?? [];
    return chiusura === 'passata' ? all.filter(dataPassata) : all;
  }, [itemsQ.data, chiusura]);

  const aperte = aperteQ.data?.items ?? [];
  const valore = aperte.reduce((s, d) => s + d.value, 0);
  const passate = aperte.filter(dataPassata);
  const valorePassate = passate.reduce((s, d) => s + d.value, 0);
  const n = aperteQ.data?.total ?? aperte.length;

  // I filtri accesi, in parole, per la riga "stai vedendo".
  const accesi = [
    chiusura === 'passata' && 'con la data di chiusura già passata',
    solo === 'senza-data' && 'senza data di chiusura',
    solo === 'azione-ritardo' && 'con la prossima azione in ritardo',
    solo === 'senza-azione' && 'senza una prossima azione',
    solo === 'senza-valore' && 'senza valore',
    stale && `ferme da più di ${summary?.staleDays ?? 14} giorni`,
    from && to && `che si chiudono fra ${periodo(from, to)}`,
    owner && 'di una persona sola',
    fascia && `della fascia ${fascia}`,
  ].filter(Boolean) as string[];

  // La striscia "quando si chiudono": prima quello che va sistemato. Se nei
  // prossimi mesi non c'è nessuna data, i tre periodi diventano una tessera
  // sola che lo dice, invece di tre zeri in fila.
  type Tessera = { k: string; label: string; n: number; amount?: number; tone?: 'warn' | 'mute'; on: boolean; toggle?: () => void; sub?: string; title?: string };
  const periodiVuoti = !!summary && summary.byPeriod.every((x) => x.count === 0);
  const ultimo = summary?.byPeriod[summary.byPeriod.length - 1];
  const tessere: Tessera[] = summary ? [
    { k: 'passata', label: 'Data già passata', sub: 'da aggiornare', n: passate.length, amount: valorePassate, tone: passate.length > 0 ? 'warn' : 'mute', on: chiusura === 'passata', toggle: () => setParams({ chiusura: chiusura === 'passata' ? '' : 'passata', from: '', to: '', solo: '' }) },
    ...(periodiVuoti
      ? [{ k: 'periodi', label: 'Nei prossimi mesi', n: 0, tone: 'mute' as const, on: false, title: ultimo ? `Nessuna trattativa con la chiusura prevista da oggi a ${MESI[Number(ultimo.to.slice(5, 7)) - 1]} ${ultimo.to.slice(0, 4)}` : undefined }]
      : summary.byPeriod.map((x) => ({
          k: x.key, label: periodoBreve(x.from, x.to), n: x.count, amount: x.amount, tone: x.count === 0 ? ('mute' as const) : undefined,
          title: `Chiusura prevista fra ${periodo(x.from, x.to)}`,
          on: from === x.from && to === x.to,
          toggle: () => setParams(from === x.from && to === x.to ? { from: '', to: '' } : { from: x.from, to: x.to, chiusura: '', solo: '' }),
        }))),
    { k: 'senza', label: 'Senza data', n: summary.undated.count, amount: summary.undated.amount, tone: summary.undated.count === 0 ? 'mute' : undefined, on: solo === 'senza-data', toggle: () => setParams({ solo: solo === 'senza-data' ? '' : 'senza-data', from: '', to: '', chiusura: '' }) },
  ] : [];

  return (
    <div className="page tr2">
      <header className="tr2-hd">
        <div className="tr2-hd-main">
          <SectionMark icon="handshake" className="tr2-sez" />
          <div className="min-w-0">
            <h1 className="tr2-title">Trattative</h1>
            <p className="tr2-risposta">
              {!aperteQ.data ? 'Conto le trattative…'
                : n === 0 ? `Nessuna trattativa aperta${pipeline ? ` in ${pipeline.name}` : ''}.`
                : <>
                    <strong>{n} {n === 1 ? 'trattativa aperta vale' : 'trattative aperte valgono'} {euro(valore)}</strong>
                    {pipeline ? <> in {pipeline.name}</> : null}.
                  </>}
            </p>
          </div>
        </div>
        {canWrite && (
          <button type="button" className="btn btn-primary tr2-nuova" onClick={() => openModal('newDeal', pipeline ? { pipeline: pipeline.slug } : undefined)}>
            <Icon name="plus" /> Nuova trattativa
          </button>
        )}
      </header>

      {/* Quando si chiudono: le date, contate senza fingere che il passato sia futuro. */}
      {tessere.length > 0 && (
        <section className="tr2-quando" aria-label="Quando si chiudono">
          {tessere.map((t) => (
            <button key={t.k} type="button" title={t.title} className={`tr2-tessera${t.tone ? ` is-${t.tone}` : ''}${t.on ? ' is-on' : ''}`} aria-pressed={t.on} onClick={t.toggle} disabled={!t.toggle || (t.n === 0 && !t.on)}>
              <span className="l">{t.label}</span>
              {t.n === 0
                ? <span className="v is-zero">{t.k === 'periodi' ? 'nessuna data prevista' : 'nessuna'}</span>
                : <span className="v">{euro(t.amount ?? 0)}</span>}
              {t.n > 0 && <span className="s">{t.n} {t.n === 1 ? 'trattativa' : 'trattative'}{t.sub ? `, ${t.sub}` : ''}</span>}
            </button>
          ))}
          {(summary?.stale ?? 0) > 0 && (
            <button type="button" className={`tr2-tessera is-call${stale ? ' is-on' : ''}`} aria-pressed={stale} onClick={() => setParams({ stale: stale ? '' : '1' })}>
              <span className="l"><Icon name="phone" size={12} /> Da richiamare</span>
              <span className="v">{summary!.stale}</span>
              <span className="s">ferme da più di {summary!.staleDays} giorni</span>
            </button>
          )}
        </section>
      )}

      <div className="tr2-bar">
        <div className="tr2-seg" role="tablist" aria-label="Pipeline">
          <button type="button" role="tab" aria-selected={!pipelineParam} className="tr2-seg-btn" onClick={() => scegliPipeline('')}>Tutte</button>
          {/* Le pipeline vuote non si mostrano (si gestiscono dalla rotella): meno scelte, stesse cose. */}
          {(pipelines ?? []).filter((x) => x.active !== false && ((x.dealsCount ?? 0) > 0 || x.slug === pipelineParam)).map((x) => (
            <button key={x.id} type="button" role="tab" aria-selected={pipelineParam === x.slug} className="tr2-seg-btn" onClick={() => scegliPipeline(x.slug)}>
              {x.name}
            </button>
          ))}
        </div>
        <label className="tr2-search">
          <Icon name="search" size={13} />
          <input aria-label="Cerca fra le trattative" placeholder="Cerca azienda, persona o telefono" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="spacer" />
        <div className="tr2-viste" role="group" aria-label="Come vedere le trattative">
          <button type="button" aria-pressed={layout === 'kanban'} disabled={!pipeline} title={pipeline ? 'Bacheca a colonne' : 'Scegli una pipeline: le colonne sono le sue fasi'} aria-label="Bacheca" onClick={() => setLayout('kanban')}><Icon name="kanban" size={15} /></button>
          <button type="button" aria-pressed={layout === 'list'} title="Lista" aria-label="Lista" onClick={() => setLayout('list')}><Icon name="list" size={15} /></button>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-ghost btn-icon"
          title="Cambia le fasi delle pipeline"
          aria-label="Cambia le fasi delle pipeline"
          onClick={() => navigate(`/trattative/pipeline${pipelineParam ? `?p=${encodeURIComponent(pipelineParam)}` : ''}`)}
        >
          <Icon name="settings" size={14} />
        </button>
      </div>

      {accesi.length > 0 && (
        <div className="tr2-filtro">
          <Icon name="filter" size={12} /> Stai vedendo solo le trattative {accesi.join(', ')}.
          <button type="button" onClick={togliFiltri}>Mostra tutte</button>
        </div>
      )}

      {/* In corso / vinte / chiuse: solo per la lista (nella bacheca sono già colonne).
          Sta sopra la lista e non nella barra, che a 1280 non ci stava in una riga. */}
      {layout === 'list' && (
        <div className="tr2-mini-seg tr2-lista-viste" role="group" aria-label="Quali trattative">
          {VISTE_LISTA.map((v) => (
            <button key={v.k} type="button" aria-pressed={view === v.k} onClick={() => setParams({ view: v.k === 'aperte' ? '' : v.k })}>{v.label}</button>
          ))}
        </div>
      )}

      {itemsQ.isLoading && !itemsQ.data ? (
        <div className="skeleton-col"><span className="skeleton sk-block" /><span className="skeleton sk-block" /><span className="skeleton sk-block" /></div>
      ) : layout === 'kanban' && pipeline ? (
        <Bacheca
          pipeline={pipeline}
          items={items}
          queryKey={dealsListQuery(params).queryKey}
          canWrite={canWrite}
          onOpen={(id) => navigate(`/trattative/${id}`)}
        />
      ) : items.length === 0 ? (
        <EmptyState
          brand
          title={accesi.length || search.trim() ? 'Nessuna trattativa con questi filtri' : 'Nessuna trattativa qui'}
          description={accesi.length || search.trim() ? 'Togli un filtro o cambia la ricerca.' : 'Le trattative compaiono qui appena ne crei una.'}
          action={accesi.length ? <button className="btn" onClick={togliFiltri}>Mostra tutte</button> : undefined}
        />
      ) : (
        <Lista items={items} mostraPipeline={!pipeline} canWrite={canWrite} onOpen={(id) => navigate(`/trattative/${id}`)} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La bacheca: una colonna per fase, col totale. Le vuote sono una striscia.
// ════════════════════════════════════════════════════════════════════
function Bacheca({ pipeline, items, queryKey, canWrite, onOpen }: {
  pipeline: Pipeline;
  items: DealItem[];
  queryKey: readonly unknown[];
  canWrite: boolean;
  onOpen: (id: string) => void;
}) {
  const { kanbanMove } = useDealActions();
  const [pendingClose, setPendingClose] = useState<{ deal: DealItem; position: number } | null>(null);
  const fasi = pathStages(pipeline);
  const uscite = exitStages(pipeline);
  const byId = useMemo(() => new Map(pipeline.stages.map((s) => [s.id, s])), [pipeline]);

  const colonne = useMemo(() => {
    const m = new Map<string, DealItem[]>();
    for (const s of fasi) m.set(s.id, []);
    m.set(CHIUSE, []);
    for (const d of items) m.get(d.isClosed ? CHIUSE : d.stageId)?.push(d);
    for (const [, arr] of m) arr.sort((a, b) => a.order - b.order);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pipeline.id]);

  const orderFor = (col: string, position: number, skipId: string) => {
    const vicini = (colonne.get(col) ?? []).filter((d) => d.id !== skipId);
    const q = Math.max(0, Math.min(position, vicini.length));
    if (vicini.length === 0) return 1000;
    if (q <= 0) return vicini[0]!.order - 1000;
    if (q >= vicini.length) return vicini[vicini.length - 1]!.order + 1000;
    return (vicini[q - 1]!.order + vicini[q]!.order) / 2;
  };

  const dnd = useKanbanDnd<string>((dealId, col, position) => {
    if (!canWrite) return;
    const deal = items.find((d) => d.id === dealId);
    if (!deal) return;
    if (col === CHIUSE) {
      if (deal.isClosed) return;
      setPendingClose({ deal, position });
      return;
    }
    const stage = byId.get(col);
    if (!stage) return;
    kanbanMove.mutate({
      id: dealId, stageId: stage.id, stageName: stage.name, outcome: stage.outcome,
      position, order: orderFor(col, position, dealId),
      prevStageId: deal.stageId, title: counterparty(deal), queryKey,
    });
  });
  const trascina = dnd.isAnyDragging();
  const cols = [...fasi.map((s) => s.id), CHIUSE];

  return (
    <>
      <div className="tr2-board">
        {cols.map((c) => {
          const schede = colonne.get(c) ?? [];
          const chiuse = c === CHIUSE;
          const stage = byId.get(c);
          const totale = schede.reduce((s, d) => s + d.value, 0);
          const vuota = schede.length === 0;
          const nome = chiuse ? 'Chiuse' : stage?.name ?? '';
          return (
            <div
              key={c}
              className={`tr2-col${vuota && !trascina ? ' is-vuota' : ''}${dnd.isOverColumn(c) ? ' is-over' : ''}${stage?.outcome === 'VINTO' ? ' is-vinta' : ''}${chiuse ? ' is-chiuse' : ''}`}
              {...(canWrite ? dnd.columnProps(c) : {})}
              title={vuota ? `${nome}: nessuna trattativa` : undefined}
            >
              {vuota && !trascina ? (
                <div className="tr2-col-strip"><span>{nome}</span></div>
              ) : (
                <>
                  <div className="tr2-col-hd">
                    <div className="tr2-col-t">{nome}<span className="n">{schede.length}</span></div>
                    {!chiuse && <div className="tr2-col-tot">{totale > 0 ? euro(totale) : <span className="mute">senza valore</span>}</div>}
                  </div>
                  <div className="tr2-col-body">
                    {trascina && <div {...dnd.dropZoneProps(c, 0)} className={`kanban-dropzone${dnd.isOverZone(c, 0) ? ' is-over' : ''}`} />}
                    {schede.map((d, i) => (
                      <Fragment key={d.id}>
                        <Scheda d={d} canWrite={canWrite} dnd={dnd} col={c} onOpen={onOpen} />
                        {trascina && !chiuse && <div {...dnd.dropZoneProps(c, i + 1)} className={`kanban-dropzone${dnd.isOverZone(c, i + 1) ? ' is-over' : ''}`} />}
                      </Fragment>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {pendingClose && (
        <div className="fixed inset-0 z-[62] flex items-start justify-center pt-[18vh]">
          <div className="relative">
            <ClosePopover
              title={counterparty(pendingClose.deal)}
              stages={uscite}
              pending={kanbanMove.isPending}
              onClose={() => setPendingClose(null)}
              onConfirm={(v) => {
                const { deal, position } = pendingClose;
                setPendingClose(null);
                kanbanMove.mutate({
                  id: deal.id, stageId: v.stageId, stageName: v.stageName, outcome: 'PERSO',
                  position, order: orderFor(CHIUSE, position, deal.id),
                  prevStageId: deal.stageId, title: counterparty(deal), lostReason: v.lostReason, queryKey,
                });
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

/** Una scheda: chi è, quanto vale, cosa si fa adesso. */
function Scheda({ d, canWrite, dnd, col, onOpen }: {
  d: DealItem;
  canWrite: boolean;
  dnd: ReturnType<typeof useKanbanDnd<string>>;
  col: string;
  onOpen: (id: string) => void;
}) {
  const azienda = leggibile(counterparty(d));
  const persona = d.contactName && leggibile(d.contactName) !== azienda ? leggibile(d.contactName) : null;
  // I requisiti del bando: in rosso solo quello che MANCA davvero; "da verificare"
  // (lo stato di partenza di tutti) si dice solo a chi è alla fase prima della
  // verifica. Prima ogni scheda della bacheca era rossa per tre righe.
  const req: Array<[string, string]> = [['connettività', d.reqConnettivita], ['polizza', d.reqPolizza], ['DURC', d.reqDurc]];
  const manca = req.filter(([, s]) => s === 'MANCA').map(([l]) => l);
  const daVerificare = req.filter(([, s]) => s === 'DA_VERIFICARE').map(([l]) => l);
  const vicinaVerifica = /accettat/i.test(d.stage.name);
  return (
    <div className="tr2-card" {...(canWrite ? dnd.cardProps(d.id, col) : {})} onClick={() => onOpen(d.id)}>
      {dataPassata(d) && <span className="tr2-card-dot" title={`Chiusura prevista il ${fmtDate(d.expectedCloseDate)}: da aggiornare`} aria-label="Data di chiusura da aggiornare" />}
      <div className="tr2-card-hd">
        <BrandLogo name={azienda} size={28} />
        <div className="min-w-0">
          <div className="t">{azienda}</div>
          {persona && <div className="s">{persona}</div>}
        </div>
      </div>
      <div className="tr2-card-v">{d.value > 0 ? euro(d.value) : <span className="mute">valore da scrivere</span>}</div>
      {d.renewalOf && <div className="tr2-card-nota">Già cliente: oggi paga {euro(d.renewalOf.annuo)} l’anno</div>}
      {d.isVoucher && d.isOpen && manca.length > 0 && <div className="tr2-card-nota is-warn">Manca: {manca.join(', ')}</div>}
      {d.isVoucher && d.isOpen && manca.length === 0 && vicinaVerifica && daVerificare.length > 0 && (
        <div className="tr2-card-nota">Da verificare: {daVerificare.join(', ')}</div>
      )}
      {d.isClosed && d.lostReason && <div className="tr2-card-nota" title={d.lostReason}>{d.stage.name}: {d.lostReason}</div>}
      {d.isOpen && <ProssimaAzione d={d} azienda={azienda} canWrite={canWrite} />}
    </div>
  );
}

/** La prossima azione è un task vero: chi chiama chi e quando. Se manca, si programma da qui. */
function ProssimaAzione({ d, azienda, canWrite }: { d: DealItem; azienda: string; canWrite: boolean }) {
  const { nextAction } = useDealActions();
  const [apri, setApri] = useState(false);
  if (d.nextTask) {
    const q = quando(d.nextTask.dueDate);
    return (
      <div className={`tr2-azione${q.alert ? ' is-alert' : ''}`} title={d.nextTask.title}>
        <Icon name="calendarClock" size={12} />
        <span className="t">{d.nextTask.title}</span>
        <span className="q">{q.testo}</span>
        {d.nextTask.assignee && <Avatar initials={d.nextTask.assignee.initials} size={18} name={fullName(d.nextTask.assignee)} />}
      </div>
    );
  }
  if (!canWrite) return <div className="tr2-azione is-vuota">Nessuna prossima azione</div>;
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <button type="button" className="tr2-programma" draggable={false} onClick={() => setApri(true)}>
        <Icon name="calendarPlus" size={12} /> Programma la prossima azione
      </button>
      {apri && (
        <NextActionPopover
          company={azienda}
          pending={nextAction.isPending}
          onClose={() => setApri(false)}
          onConfirm={(v) => { setApri(false); nextAction.mutate({ id: d.id, ...v }); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La lista: per "Tutte le pipeline" e per chi preferisce le righe.
// ════════════════════════════════════════════════════════════════════
function Lista({ items, mostraPipeline, canWrite, onOpen }: { items: DealItem[]; mostraPipeline: boolean; canWrite: boolean; onOpen: (id: string) => void }) {
  return (
    <div className="tr2-lista" role="list">
      {items.map((d) => <Riga key={d.id} d={d} mostraPipeline={mostraPipeline} canWrite={canWrite} onOpen={onOpen} />)}
    </div>
  );
}

function Riga({ d, mostraPipeline, canWrite, onOpen }: { d: DealItem; mostraPipeline: boolean; canWrite: boolean; onOpen: (id: string) => void }) {
  const { data: pipelines } = useQuery(pipelinesQuery);
  const { win, close, movePipeline } = useDealActions();
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState(false);
  const azienda = leggibile(counterparty(d));
  const persona = d.contactName && leggibile(d.contactName) !== azienda ? leggibile(d.contactName) : null;
  const full = (pipelines ?? []).find((x) => x.id === d.pipelineId);
  const vinta = pathStages(full).find((s) => s.outcome === 'VINTO');
  const perse = exitStages(full);
  const q = d.expectedCloseDate ? quando(d.expectedCloseDate) : null;

  return (
    <div className="tr2-riga" role="listitem" tabIndex={0} onClick={() => onOpen(d.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(d.id); }}>
      <BrandLogo name={azienda} size={32} />
      <span className="tr2-riga-chi">
        <span className="t">{azienda}</span>
        <span className="s">{[persona, d.renewalOf ? `già cliente, ${euro(d.renewalOf.annuo)} l’anno` : null].filter(Boolean).join(' · ') || ' '}</span>
      </span>
      <span className="tr2-riga-fase">
        <span className={`tr2-fase${d.isWon ? ' is-vinta' : d.isClosed ? ' is-chiusa' : ''}`}>{d.stage.name}</span>
        {mostraPipeline && <span className="p">{d.pipeline.name}</span>}
      </span>
      <span className="tr2-riga-v">{d.value > 0 ? euro(d.value) : <span className="mute">da scrivere</span>}</span>
      <span className="tr2-riga-az" onClick={(e) => e.stopPropagation()}>
        {d.isOpen ? <ProssimaAzione d={d} azienda={azienda} canWrite={canWrite} /> : <span className="mute">—</span>}
      </span>
      <span className={`tr2-riga-data${dataPassata(d) ? ' is-warn' : ''}`}>
        {!d.isOpen ? (d.expectedCloseDate ? fmtDate(d.expectedCloseDate) : '') : dataPassata(d) ? 'data da aggiornare' : q ? `si chiude ${q.testo}` : 'senza data'}
      </span>
      <span onClick={(e) => e.stopPropagation()} className="relative">
        {canWrite && (
          <ActionsMenu
            label={`Azioni su ${azienda}`}
            items={[
              { key: 'open', label: 'Apri', icon: 'arrowRight', onClick: () => onOpen(d.id) },
              { key: 'win', label: vinta ? `Segna ${vinta.name.toLowerCase()}` : 'Segna vinta', icon: 'check', hidden: !vinta || d.isWon, onClick: () => win.mutate({ id: d.id, title: azienda, stageId: vinta!.id, stageName: vinta!.name, prevStageId: d.stageId }) },
              { key: 'move', label: 'Sposta in un’altra pipeline', icon: 'swap', onClick: () => setMoving(true) },
              { key: 'close', label: 'Chiudi come persa', icon: 'x', danger: true, hidden: d.isClosed || !perse.length, onClick: () => setClosing(true) },
            ]}
          />
        )}
        {closing && (
          <ClosePopover
            title={azienda}
            stages={perse}
            pending={close.isPending}
            onClose={() => setClosing(false)}
            onConfirm={(v) => { setClosing(false); close.mutate({ id: d.id, title: azienda, prevStageId: d.stageId, ...v }); }}
          />
        )}
        {moving && (
          <MovePipelinePopover
            title={azienda}
            currentPipelineId={d.pipelineId}
            pending={movePipeline.isPending}
            onClose={() => setMoving(false)}
            onConfirm={(v) => { setMoving(false); movePipeline.mutate({ id: d.id, title: azienda, ...v }); }}
          />
        )}
      </span>
    </div>
  );
}
